import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { getBackendOrigin } from '../lib/api';

const EMPTY = { name: '', email: '', company: '', phone: '', message: '', website: '' };

export default function EnterpriseEnquiryDialog({ open, onOpenChange }) {
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const setField = (key) => (e) => setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const close = (next) => {
    onOpenChange(next);
    if (!next) {
      setError('');
      setDone(false);
      setForm(EMPTY);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const origin = getBackendOrigin() || '';
      await axios.post(`${origin}/api/public/enterprise-enquiry`, {
        ...form,
        source: 'app_pricing_enterprise',
      });
      setDone(true);
    } catch (err) {
      setError(
        typeof err.response?.data?.detail === 'string'
          ? err.response.data.detail
          : 'Could not send enquiry. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Contact sales</DialogTitle>
          <DialogDescription>
            Tell us about your rollout. We’ll reply from business@omnichek.com.
          </DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Thanks — we received your enquiry and will get back to you shortly.
            </p>
            <DialogFooter>
              <Button type="button" onClick={() => close(false)}>Close</Button>
            </DialogFooter>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="hidden" aria-hidden="true">
              <input tabIndex={-1} autoComplete="off" value={form.website} onChange={setField('website')} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ent-name">Name</Label>
              <Input id="ent-name" required value={form.name} onChange={setField('name')} autoComplete="name" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ent-email">Work email</Label>
              <Input id="ent-email" type="email" required value={form.email} onChange={setField('email')} autoComplete="email" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ent-company">Company</Label>
              <Input id="ent-company" required value={form.company} onChange={setField('company')} autoComplete="organization" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ent-phone">Phone (optional)</Label>
              <Input id="ent-phone" value={form.phone} onChange={setField('phone')} autoComplete="tel" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="ent-message">Message</Label>
              <Textarea
                id="ent-message"
                required
                rows={4}
                value={form.message}
                onChange={setField('message')}
                placeholder="Plants, SKU volume, on-prem or SaaS, timeline…"
              />
            </div>
            {error ? <p className="text-sm text-red-600">{error}</p> : null}
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => close(false)}>Cancel</Button>
              <Button type="submit" disabled={submitting} className="bg-brand-600 hover:bg-brand-700 text-white">
                {submitting ? 'Sending…' : 'Send enquiry'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
